import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  LocalGasStationOutlined as LocalGasStationOutlinedIcon,
  OpacityOutlined as OpacityOutlinedIcon,
  Refresh as RefreshIcon,
  ShowChartOutlined as ShowChartOutlinedIcon,
} from "@mui/icons-material";

type DashboardMeasurement = {
  "@id"?: string;
  id?: number;
  amount?: number | null;
  date?: string | null;
  updatedAt?: string | null;
};

type DashboardBunkering = {
  "@id"?: string;
  id?: number;
  amount?: number | null;
  date?: string | null;
  supplier?: string | null;
};

type VesselGasoilDashboard = {
  vessel?: {
    id?: number | string;
    name?: string | null;
  } | null;
  currentOnboard?: DashboardMeasurement | null;
  lastBunkering?: DashboardBunkering | null;
  staleDays?: number | null;
  measurements?: DashboardMeasurement[];
  bunkerings?: DashboardBunkering[];
};

type Props = {
  vessel?: any;
  dashboard?: VesselGasoilDashboard | null;
  callback?: () => Promise<void> | void;
  onClose?: () => void;
};

type ChartPoint = {
  id: string;
  amount: number;
  date: string;
  timestamp: number;
  x: number;
  y: number;
};

const API_URL = process.env.REACT_APP_API_URL;

const normalizeCollection = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.["hydra:member"])) return value["hydra:member"];
  if (Array.isArray(value?.member)) return value.member;
  if (Array.isArray(value?.items)) return value.items;

  return [];
};

const getEntityId = (value?: any): string => {
  if (!value) return "";

  if (typeof value === "string") {
    return value.split("/").filter(Boolean).pop() || value;
  }

  if (value["@id"]) {
    return (
      String(value["@id"]).split("/").filter(Boolean).pop() ||
      String(value["@id"])
    );
  }

  if (value.id !== undefined && value.id !== null) {
    return String(value.id);
  }

  return "";
};

const getApiIri = (
  resource: string,
  id?: string | number | null
): string => {
  if (!id) return "";

  const stringId = String(id);

  if (stringId.startsWith("/api/")) {
    return stringId;
  }

  if (
    stringId.startsWith("http://") ||
    stringId.startsWith("https://")
  ) {
    return stringId;
  }

  return `/api/${resource}/${stringId}`;
};

const getVesselId = (vessel?: any): string => {
  return getEntityId(vessel);
};

const getVesselName = (vessel?: any): string => {
  return vessel?.name || vessel?.abbreviation || "";
};

const numberFormat = new Intl.NumberFormat("nl-BE", {
  maximumFractionDigits: 0,
});

const compactNumberFormat = new Intl.NumberFormat("nl-BE", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const formatNumber = (value?: number | null): string => {
  const parsed = Number(value ?? 0);

  return numberFormat.format(Number.isFinite(parsed) ? parsed : 0);
};

const formatCompactNumber = (value?: number | null): string => {
  const parsed = Number(value ?? 0);

  return compactNumberFormat.format(Number.isFinite(parsed) ? parsed : 0);
};

const formatDateOnly = (value?: string | null): string => {
  if (!value) return "—";

  const date = dayjs(value);

  return date.isValid() ? date.format("DD/MM/YYYY") : "—";
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return "—";

  const date = dayjs(value);

  return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "—";
};

const buildDashboard = ({
  vesselId,
  vesselName,
  measurements,
  bunkerings,
}: {
  vesselId?: string | number | null;
  vesselName?: string | null;
  measurements: DashboardMeasurement[];
  bunkerings: DashboardBunkering[];
}): VesselGasoilDashboard => {
  const sortedMeasurements = [...measurements].sort((a, b) => {
    return dayjs(b.date || 0).valueOf() - dayjs(a.date || 0).valueOf();
  });

  const sortedBunkerings = [...bunkerings].sort((a, b) => {
    return dayjs(b.date || 0).valueOf() - dayjs(a.date || 0).valueOf();
  });

  const currentOnboard = sortedMeasurements[0] || null;
  const lastBunkering = sortedBunkerings[0] || null;

  const staleDays =
    currentOnboard?.date && dayjs(currentOnboard.date).isValid()
      ? Math.max(dayjs().diff(dayjs(currentOnboard.date), "day"), 0)
      : null;

  return {
    vessel: {
      id: vesselId || undefined,
      name: vesselName || null,
    },
    currentOnboard,
    lastBunkering,
    staleDays,
    measurements: sortedMeasurements,
    bunkerings: sortedBunkerings,
  };
};

const StatCard: FC<{
  title: string;
  icon: React.ReactNode;
  value: string;
  subtitle?: string;
  secondaryLine?: string;
  accentColor: string;
}> = ({
  title,
  icon,
  value,
  subtitle,
  secondaryLine,
  accentColor,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        minHeight: 170,
        p: 2.5,
        borderRadius: "14px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        backgroundColor: "#ffffff",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.07)",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 900,
            color: "#1e293b",
          }}
        >
          {title}
        </Typography>

        <Avatar
          sx={{
            width: 44,
            height: 44,
            backgroundColor: `${accentColor}18`,
            color: accentColor,
          }}
        >
          {icon}
        </Avatar>
      </Stack>

      <Typography
        sx={{
          mt: 3.25,
          fontSize: {
            xs: 25,
            md: 28,
          },
          fontWeight: 950,
          color: accentColor,
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>

      {subtitle ? (
        <Typography
          sx={{
            mt: 1.25,
            color: "text.secondary",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {subtitle}
        </Typography>
      ) : null}

      {secondaryLine ? (
        <Typography
          noWrap
          sx={{
            mt: 0.75,
            fontSize: 14,
            fontWeight: 850,
            color: "#1e293b",
          }}
        >
          {secondaryLine}
        </Typography>
      ) : null}
    </Paper>
  );
};

const GasoilHistoryChart: FC<{
  measurements: DashboardMeasurement[];
  bunkerings: DashboardBunkering[];
}> = ({ measurements, bunkerings }) => {
  const { t } = useTranslation();

  const chartWidth = 1000;
  const chartHeight = 360;

  const margin = {
    top: 28,
    right: 30,
    bottom: 62,
    left: 82,
  };

  const innerWidth = chartWidth - margin.left - margin.right;
  const innerHeight = chartHeight - margin.top - margin.bottom;

  const chartData = useMemo(() => {
    return measurements
      .filter((measurement) => {
        const amount = Number(measurement.amount);
        const timestamp = dayjs(measurement.date).valueOf();

        return (
          Number.isFinite(amount) &&
          measurement.date &&
          Number.isFinite(timestamp)
        );
      })
      .map((measurement, index) => ({
        id:
          measurement["@id"] ||
          String(measurement.id ?? `${measurement.date}-${index}`),
        amount: Number(measurement.amount),
        date: String(measurement.date),
        timestamp: dayjs(measurement.date).valueOf(),
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [measurements]);

  const validBunkerings = useMemo(() => {
    return bunkerings
      .filter((bunkering) => {
        return (
          bunkering.date &&
          dayjs(bunkering.date).isValid() &&
          Number.isFinite(Number(bunkering.amount))
        );
      })
      .map((bunkering, index) => ({
        id:
          bunkering["@id"] ||
          String(bunkering.id ?? `${bunkering.date}-${index}`),
        date: String(bunkering.date),
        timestamp: dayjs(bunkering.date).valueOf(),
        amount: Number(bunkering.amount),
        supplier: bunkering.supplier || "",
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [bunkerings]);

  const chartValues = useMemo(() => {
    if (!chartData.length) {
      return {
        points: [] as ChartPoint[],
        minTimestamp: 0,
        maxTimestamp: 0,
        yMaximum: 0,
        yTicks: [] as number[],
      };
    }

    const minTimestamp = chartData[0].timestamp;
    const maxTimestamp = chartData[chartData.length - 1].timestamp;

    const maximumAmount = Math.max(
      ...chartData.map((point) => point.amount),
      1
    );

    const yMaximum = Math.ceil((maximumAmount * 1.15) / 1000) * 1000 || 1000;

    const xScale = (timestamp: number): number => {
      if (minTimestamp === maxTimestamp) {
        return margin.left + innerWidth / 2;
      }

      return (
        margin.left +
        ((timestamp - minTimestamp) / (maxTimestamp - minTimestamp)) *
          innerWidth
      );
    };

    const yScale = (amount: number): number => {
      return (
        margin.top +
        innerHeight -
        (amount / yMaximum) * innerHeight
      );
    };

    const points: ChartPoint[] = chartData.map((point) => ({
      ...point,
      x: xScale(point.timestamp),
      y: yScale(point.amount),
    }));

    const yTicks = Array.from(
      {
        length: 5,
      },
      (_, index) => (yMaximum / 4) * index
    );

    return {
      points,
      minTimestamp,
      maxTimestamp,
      yMaximum,
      yTicks,
    };
  }, [chartData, innerHeight, innerWidth, margin.left, margin.top]);

  const {
    points,
    minTimestamp,
    maxTimestamp,
    yMaximum,
    yTicks,
  } = chartValues;

  const linePath = useMemo(() => {
    if (!points.length) return "";

    return points
      .map((point, index) => {
        return `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`;
      })
      .join(" ");
  }, [points]);

  const areaPath = useMemo(() => {
    if (!points.length) return "";

    const bottomY = margin.top + innerHeight;
    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];

    return [
      `M ${firstPoint.x} ${bottomY}`,
      ...points.map((point) => `L ${point.x} ${point.y}`),
      `L ${lastPoint.x} ${bottomY}`,
      "Z",
    ].join(" ");
  }, [points, innerHeight, margin.top]);

  const xAxisLabels = useMemo(() => {
    if (!points.length) return [];

    if (points.length === 1) {
      return [points[0]];
    }

    const maximumLabels = 6;
    const labelCount = Math.min(maximumLabels, points.length);
    const indexes = new Set<number>();

    for (let index = 0; index < labelCount; index += 1) {
      indexes.add(
        Math.round((index * (points.length - 1)) / (labelCount - 1))
      );
    }

    return Array.from(indexes).map((index) => points[index]);
  }, [points]);

  const getBunkeringX = (timestamp: number): number | null => {
    if (!points.length) return null;

    if (minTimestamp === maxTimestamp) {
      return margin.left + innerWidth / 2;
    }

    if (timestamp < minTimestamp || timestamp > maxTimestamp) {
      return null;
    }

    return (
      margin.left +
      ((timestamp - minTimestamp) / (maxTimestamp - minTimestamp)) *
        innerWidth
    );
  };

  if (!chartData.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          minHeight: 320,
          borderRadius: "14px",
          border: "1px solid rgba(15, 23, 42, 0.08)",
          backgroundColor: "#ffffff",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.07)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Alert severity="info" sx={{ width: "100%" }}>
          {t(
            "noGasoilMeasurements",
            "No gasoil measurements found."
          )}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: {
          xs: 1.5,
          sm: 2.5,
        },
        borderRadius: "14px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        backgroundColor: "#ffffff",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.07)",
      }}
    >
      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        justifyContent="space-between"
        alignItems={{
          xs: "flex-start",
          sm: "center",
        }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: "rgba(37, 99, 235, 0.1)",
              color: "#2563eb",
            }}
          >
            <ShowChartOutlinedIcon />
          </Avatar>

          <Box>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 950,
                color: "#1e293b",
              }}
            >
              {t("gasoilHistory", "Gasoil history")}
            </Typography>

            <Typography
              sx={{
                mt: 0.15,
                fontSize: 12.5,
                fontWeight: 700,
                color: "text.secondary",
              }}
            >
              {t(
                "gasoilHistoryDescription",
                "Recorded onboard gasoil measurements over time"
              )}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Chip
            size="small"
            label={t("measurementsCount", {
              count: chartData.length,
              defaultValue: `${chartData.length} measurements`,
            })}
            sx={{
              fontWeight: 800,
              backgroundColor: "#dbeafe",
              color: "#1d4ed8",
            }}
          />

          {validBunkerings.length > 0 ? (
            <Chip
              size="small"
              label={t("bunkeringsCount", {
                count: validBunkerings.length,
                defaultValue: `${validBunkerings.length} bunkerings`,
              })}
              sx={{
                fontWeight: 800,
                backgroundColor: "#dcfce7",
                color: "#15803d",
              }}
            />
          ) : null}
        </Stack>
      </Stack>

      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Box
          sx={{
            minWidth: {
              xs: 720,
              md: "100%",
            },
          }}
        >
          <svg
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            width="100%"
            height="auto"
            role="img"
            aria-label={t(
              "gasoilHistory",
              "Gasoil history"
            )}
            style={{
              display: "block",
              overflow: "visible",
            }}
          >
            <defs>
              <linearGradient
                id="gasoilChartGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor="#2563eb"
                  stopOpacity="0.28"
                />
                <stop
                  offset="100%"
                  stopColor="#2563eb"
                  stopOpacity="0.02"
                />
              </linearGradient>
            </defs>

            {yTicks.map((tick) => {
              const y =
                margin.top +
                innerHeight -
                (tick / yMaximum) * innerHeight;

              return (
                <g key={`y-tick-${tick}`}>
                  <line
                    x1={margin.left}
                    x2={chartWidth - margin.right}
                    y1={y}
                    y2={y}
                    stroke="#e2e8f0"
                    strokeWidth="1"
                    strokeDasharray="4 5"
                  />

                  <text
                    x={margin.left - 12}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="12"
                    fontWeight="700"
                    fill="#64748b"
                  >
                    {formatCompactNumber(tick)} L
                  </text>
                </g>
              );
            })}

            <line
              x1={margin.left}
              x2={margin.left}
              y1={margin.top}
              y2={margin.top + innerHeight}
              stroke="#cbd5e1"
              strokeWidth="1"
            />

            <line
              x1={margin.left}
              x2={chartWidth - margin.right}
              y1={margin.top + innerHeight}
              y2={margin.top + innerHeight}
              stroke="#cbd5e1"
              strokeWidth="1"
            />

            {validBunkerings.map((bunkering) => {
              const x = getBunkeringX(bunkering.timestamp);

              if (x === null) return null;

              return (
                <g key={`bunkering-${bunkering.id}`}>
                  <Tooltip
                    title={`${formatDateOnly(
                      bunkering.date
                    )} · +${formatNumber(bunkering.amount)} L${
                      bunkering.supplier
                        ? ` · ${bunkering.supplier}`
                        : ""
                    }`}
                    arrow
                  >
                    <line
                      x1={x}
                      x2={x}
                      y1={margin.top}
                      y2={margin.top + innerHeight}
                      stroke="#16a34a"
                      strokeWidth="2"
                      strokeDasharray="6 5"
                      opacity="0.7"
                      style={{
                        cursor: "help",
                      }}
                    />
                  </Tooltip>

                  <circle
                    cx={x}
                    cy={margin.top + 8}
                    r="5"
                    fill="#16a34a"
                    stroke="#ffffff"
                    strokeWidth="2"
                  />
                </g>
              );
            })}

            {areaPath ? (
              <path
                d={areaPath}
                fill="url(#gasoilChartGradient)"
              />
            ) : null}

            {linePath ? (
              <path
                d={linePath}
                fill="none"
                stroke="#2563eb"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : null}

            {points.map((point) => (
              <Tooltip
                key={point.id}
                arrow
                title={`${formatDateTime(point.date)} · ${formatNumber(
                  point.amount
                )} L`}
              >
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="6"
                  fill="#ffffff"
                  stroke="#2563eb"
                  strokeWidth="4"
                  style={{
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            ))}

            {xAxisLabels.map((point) => (
              <g key={`x-label-${point.id}`}>
                <line
                  x1={point.x}
                  x2={point.x}
                  y1={margin.top + innerHeight}
                  y2={margin.top + innerHeight + 6}
                  stroke="#94a3b8"
                  strokeWidth="1"
                />

                <text
                  x={point.x}
                  y={margin.top + innerHeight + 26}
                  textAnchor="middle"
                  fontSize="11.5"
                  fontWeight="700"
                  fill="#64748b"
                >
                  {dayjs(point.date).format("DD/MM/YY")}
                </text>
              </g>
            ))}
          </svg>
        </Box>
      </Box>

      {validBunkerings.length > 0 ? (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1 }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 20,
                height: 3,
                borderRadius: 999,
                backgroundColor: "#2563eb",
              }}
            />

            <Typography
              sx={{
                fontSize: 12,
                color: "text.secondary",
                fontWeight: 700,
              }}
            >
              {t("onboardMeasurement", "Onboard measurement")}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 20,
                borderTop: "2px dashed #16a34a",
              }}
            />

            <Typography
              sx={{
                fontSize: 12,
                color: "text.secondary",
                fontWeight: 700,
              }}
            >
              {t("bunkering", "Bunkering")}
            </Typography>
          </Stack>
        </Stack>
      ) : null}
    </Paper>
  );
};

const GasoilBunkeringModal: FC<Props> = ({
  vessel,
  dashboard: initialDashboard = null,
  callback,
}) => {
  const { t } = useTranslation();

  const vesselId = getVesselId(vessel);
  const vesselName = getVesselName(vessel);

  const vesselIri = useMemo(() => {
    return getApiIri("vessels", vesselId);
  }, [vesselId]);

  const [dashboard, setDashboard] =
    useState<VesselGasoilDashboard | null>(initialDashboard);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const refreshDashboard = useCallback(async () => {
    if (!vesselIri) {
      setLoadError(t("missingVessel", "Missing vessel."));
      return;
    }

    try {
      setLoading(true);
      setLoadError("");

      const [gasoilResponse, bunkeringResponse] = await Promise.all([
        axios({
          method: "get",
          url: `${API_URL}/vessel_gasoils?vessel=${encodeURIComponent(
            vesselIri
          )}&order[date]=desc&pagination=false`,
          headers: {
            accept: "application/ld+json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }),
        axios({
          method: "get",
          url: `${API_URL}/vessel_bunkerings?vessel=${encodeURIComponent(
            vesselIri
          )}&order[date]=desc&pagination=false`,
          headers: {
            accept: "application/ld+json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }),
      ]);

      const measurements = normalizeCollection(gasoilResponse.data);
      const bunkerings = normalizeCollection(bunkeringResponse.data);

      setDashboard(
        buildDashboard({
          vesselId,
          vesselName,
          measurements,
          bunkerings,
        })
      );

      if (callback) {
        await callback();
      }
    } catch (exception: any) {
      setLoadError(
        exception?.response?.data?.["hydra:description"] ||
          exception?.response?.data?.detail ||
          exception?.message ||
          t(
            "failedToLoadGasoilDashboard",
            "Failed to load gasoil dashboard."
          )
      );
    } finally {
      setLoading(false);
    }
  }, [
    callback,
    vesselIri,
    vesselId,
    vesselName,
    t,
  ]);

  useEffect(() => {
    void refreshDashboard();
  }, [refreshDashboard]);

  const measurements = dashboard?.measurements ?? [];
  const bunkerings = dashboard?.bunkerings ?? [];

  return (
    <>
      {loading ? <LinearProgress /> : null}

      <Box
        sx={{
          backgroundColor: "#f8fafc",
          mx: -2,
          my: -2,
          p: {
            xs: 2,
            md: 2.5,
          },
          minHeight: 400,
        }}
      >
        {!vesselIri ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("missingVessel", "Missing vessel.")}
          </Alert>
        ) : null}

        {loadError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        ) : null}

        {loading && !dashboard ? (
          <Box
            sx={{
              minHeight: "40vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : dashboard ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <StatCard
                title={t("currentOnboard", "Current onboard")}
                icon={<OpacityOutlinedIcon />}
                value={`${formatNumber(
                  dashboard.currentOnboard?.amount
                )} L`}
                subtitle={
                  dashboard.currentOnboard?.date
                    ? t("asOfDate", {
                        date: formatDateTime(
                          dashboard.currentOnboard.date
                        ),
                        defaultValue: `As of ${formatDateTime(
                          dashboard.currentOnboard.date
                        )}`,
                      })
                    : t(
                        "noMeasurementAvailable",
                        "No measurement available"
                      )
                }
                accentColor="#0B2E78"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StatCard
                title={t("lastBunkering", "Last bunkering")}
                icon={<LocalGasStationOutlinedIcon />}
                value={
                  dashboard.lastBunkering
                    ? `+${formatNumber(
                        dashboard.lastBunkering.amount
                      )} L`
                    : "—"
                }
                subtitle={
                  dashboard.lastBunkering?.date
                    ? formatDateOnly(
                        dashboard.lastBunkering.date
                      )
                    : t(
                        "noBunkeringAvailable",
                        "No bunkering available"
                      )
                }
                secondaryLine={
                  dashboard.lastBunkering?.supplier || undefined
                }
                accentColor="#16a34a"
              />
            </Grid>

            <Grid item xs={12}>
              <GasoilHistoryChart
                measurements={measurements}
                bunkerings={bunkerings}
              />
            </Grid>
          </Grid>
        ) : null}
      </Box>
    </>
  );
};

export default GasoilBunkeringModal;